// What onboarding shows back to the user: a summary built only from their answers, and the first
// meal, chosen by the normal recommender and described honestly about what is known.

import { ruleLabel } from './diet';
import { FOOD_LIKES, FRUSTRATIONS, GOALS, PRIORITY_LABELS, TIME_OPTIONS, type Goal } from './preferences';
import type { Recommendation } from './recommend';
import { displayName } from './taxonomy';
import { joinNatural } from './text';
import type { Profile } from '../state/schema';

export type SummaryStep = 'struggles' | 'food' | 'kitchen' | 'avoid' | 'priorities';
export interface SummaryLine { step: SummaryStep; text: string }

const lower = (s: string) => s.charAt(0).toLowerCase() + s.slice(1);

/** One line per thing the user told us. Nothing inferred, nothing invented. */
export function buildSummary(p: Profile, kitchenCount: number): SummaryLine[] {
  const out: SummaryLine[] = [];
  const time = TIME_OPTIONS.find((t) => t.value === p.maxMinutes);
  // Flexible (also what skipping means) sets no limit, so there is nothing to say about it.
  if (time && time.value !== 120) out.push({ step: 'struggles', text: `Dinners in ${lower(time.label)}` });
  for (const f of p.frustrations) {
    const text = {
      what_to_cook: 'Decides what to cook for you',
      waste: 'Uses up food before it goes to waste',
      cost: 'Keeps grocery spending down',
      time: 'Keeps cooking quick',
      goals: 'Helps you eat for your goals',
      boredom: 'Keeps meals varied',
    }[f];
    out.push({ step: 'struggles', text });
  }
  const likes = p.likes.filter((l) => l !== 'unsure');
  if (likes.length) out.push({ step: 'food', text: `Leans towards ${joinNatural(likes.map((l) => lower(FOOD_LIKES.find((x) => x.id === l)!.label)))}` });
  else if (p.likes.includes('unsure')) out.push({ step: 'food', text: 'Mixes cuisines while you find favourites' });
  if (p.likes.includes('african_caribbean')) out.push({ step: 'food', text: 'African and Caribbean dishes are being added; they’ll appear as they’re ready' });
  if (kitchenCount) out.push({ step: 'kitchen', text: `Starts from the ${kitchenCount} ${kitchenCount === 1 ? 'item' : 'items'} in your kitchen` });
  if (p.staples.length) out.push({ step: 'kitchen', text: `Counts ${p.staples.length} cupboard ${p.staples.length === 1 ? 'basic' : 'basics'} as always there` });
  if (p.rules.length) out.push({ step: 'avoid', text: `Never suggests anything that breaks: ${joinNatural(p.rules.map(ruleLabel))}` });
  if (p.dislikes.length) out.push({ step: 'avoid', text: `Leaves out ${joinNatural(p.dislikes.map(displayName))}` });
  for (const pr of p.priorities.filter((x) => x !== 'fitness')) out.push({ step: 'priorities', text: `Puts “${lower(PRIORITY_LABELS[pr].label)}” first` });
  const goals = p.goals.filter((g): g is Exclude<Goal, 'none'> => g !== 'none');
  if (goals.length) out.push({ step: 'priorities', text: `Favours dinners that suit: ${joinNatural(goals.map((g) => lower(GOALS.find((x) => x.id === g)!.label)))}` });
  return out;
}

/** Why a frustration answer matters, for the summary header. */
export function frustrationLabel(id: Profile['frustrations'][number]): string {
  return FRUSTRATIONS.find((f) => f.id === id)!.label;
}

export type FirstMealState = 'ready' | 'short' | 'unknown';

export interface FirstMeal {
  rec: Recommendation;
  state: FirstMealState;
  headline: string;
  detail: string;
}

/**
 * The free first meal: the recommender's top dinner. When the kitchen is known we prefer one that
 * can be made now; when it isn't, we say so rather than claim it can be cooked.
 */
export function pickFirstMeal(recs: Recommendation[]): FirstMeal | null {
  const mains = recs.filter((r) => r.recipe.meal === 'main');
  // A dinner they can make, else any meal they can make (breakfast, side, snack), else the closest dinner.
  const ready = (r: Recommendation) => r.kitchenMode === 'known' && r.makeable;
  const rec = mains.find(ready) ?? recs.find(ready) ?? mains[0] ?? recs[0];
  if (!rec) return null;
  if (rec.kitchenMode === 'known' && rec.makeable) {
    return { rec, state: 'ready', headline: 'Here’s something you can make right now', detail: 'You have everything it needs.' };
  }
  if (rec.kitchenMode === 'known') {
    return { rec, state: 'short', headline: 'Your first meal recommendation', detail: `You’re ${rec.missing.length === 1 ? 'one ingredient' : `${rec.missing.length} ingredients`} away.` };
  }
  return { rec, state: 'unknown', headline: 'Your first meal recommendation', detail: 'A suggestion based on what you told us. Tick what you already have.' };
}
